import { Color, Spacing } from '@persona-id/design-tokens';
import { sum, sumBy } from 'lodash';
import { VFC } from 'react';
import styled from 'styled-components';

import { NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import StepFlowBranchCurves from 'dashboard/components/StepFlow/StepFlowBranchCurves';

export const StepFlowContainer = styled('div')<{
  width: number;
  withMinWidth: boolean;
}>`
  min-width: ${({ withMinWidth }) =>
    withMinWidth ? `calc(100vw - ${NAVBAR_EXPANDED_WIDTH}px)` : 'auto'};
  width: ${({ width }) => width}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 60px;
  transform-origin: 0 0;
`;

export const StepFlowStepContainer = styled('div')<{ width: number }>`
  width: ${({ width }) => width}px;
  display: flex;
  align-items: center;
  flex-direction: column;
  min-height: auto;
`;

export const StepFlowVerticalLine = styled('div')<{
  width?: number;
  strokeWidth?: number;
  color?: string;
}>`
  ${({ width, strokeWidth, color }) => {
    const lineColor = color ?? Color.CorePurple200;
    const distanceLeft = (width ?? 10) / 2 - (strokeWidth ?? 2) / 2;
    const distanceRight = (width ?? 10) / 2 + (strokeWidth ?? 2) / 2;

    return `
      width: ${width ?? 10}px;
      flex: 1 0 0%;
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) ${distanceLeft}px,
        ${lineColor} ${distanceLeft}px,
        ${lineColor} ${distanceRight}px,
        rgba(0, 0, 0, 0) ${distanceRight}px
      );
    `;
  }}
`;

const StepLineContainer = styled('div')`
  height: ${Spacing[6]}px;
  display: flex;
  justify-content: center;
`;

export const StepFlowBottomSingleLine = ({ color }: { color?: string }) => {
  return (
    <StepLineContainer>
      <svg width='16' height='32' viewBox='0 0 2 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M 1 0 L 1 32' stroke={color || Color.CorePurple200} strokeWidth='2' />
      </svg>
    </StepLineContainer>
  );
};
interface HorizontalBranchingLineProps {
  leftOffset: number;
  rightOffset: number;
  width: number;
  leftPathColor?: string;
  rightPathColor?: string;
}

const HorizontalBranchingLine: VFC<HorizontalBranchingLineProps> = ({
  leftOffset,
  rightOffset,
  width,
  leftPathColor,
  rightPathColor,
}) => {
  return (
    <OuterLine leftOffset={leftOffset} rightOffset={rightOffset} width={width}>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <InnerLine color={leftPathColor} />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <InnerLine color={rightPathColor} />
    </OuterLine>
  );
};

const PathHorizontalBranchingLineContainer = styled('div')`
  margin-top: -2px;
`;

const OuterLine = styled('div')<{ leftOffset: number; rightOffset: number; width: number }>`
  width: ${({ width }) => width}px;
  display: flex;
  /* Note: 16 is the curve width */
  padding-left: ${({ leftOffset }) => leftOffset + 16}px;
  padding-right: ${({ rightOffset }) => rightOffset + 16}px;
`;

const InnerLine = styled('div')<{ color: string }>`
  width: 100%;
  height: 1px;
  border-top: 2px solid ${({ color }) => color || Color.CorePurple200};
`;

interface StepFlowBranchConnectorsProps {
  branchDirection: 'in' | 'out';
  childTokens: string[];
  stepTokenToPositionMap: Map<string, StepPosition>;
  width: number;
  leftPathColor?: string;
  rightPathColor?: string;
  pathToChildIndex?: number;
}

export const StepFlowBranchConnectors: VFC<StepFlowBranchConnectorsProps> = ({
  branchDirection,
  childTokens,
  stepTokenToPositionMap,
  width,
  leftPathColor,
  rightPathColor,
  pathToChildIndex,
}) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const leftOffset = stepTokenToPositionMap.get(childTokens[0]).width / 2;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const rightOffset = stepTokenToPositionMap.get(childTokens[childTokens.length - 1]).width / 2;

  // 1. find out if the child is to the left or the right of center
  // 2. if its to the left, then the right offset is half the whole thing, if its to the right
  //    then the left offset is half the width (the path is only going to go one direction from the root)
  // 3. for the other offset, we sum up all before (or after if right side) plus its half
  let pathLeftOffset = 0;
  let pathRightOffset = 0;

  const CURVE_WIDTH = 16;

  const childWidths = sum(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    childTokens.map((childToken) => stepTokenToPositionMap.get(childToken).width),
  );

  const drawPath = pathToChildIndex != null && pathToChildIndex >= 0;

  if (drawPath) {
    const leftHalf = childTokens.slice(0, pathToChildIndex);
    const rightHalf = childTokens.slice(pathToChildIndex + 1);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const leftWidthSum = sumBy(leftHalf, (token) => stepTokenToPositionMap.get(token).width);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const rightWidthSum = sumBy(rightHalf, (token) => stepTokenToPositionMap.get(token).width);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const targetWidth = stepTokenToPositionMap.get(childTokens[pathToChildIndex]).width;

    const isLeft = leftWidthSum + targetWidth / 2 <= childWidths / 2;

    if (isLeft) {
      const curveOffset = pathToChildIndex === 0 ? 0 : CURVE_WIDTH;
      // account for the existing offset applied by the horizontal line component and then add one back in to fill in path from root
      pathLeftOffset += leftWidthSum + targetWidth / 2 - curveOffset - 1;
      pathRightOffset = childWidths / 2 - CURVE_WIDTH - 1;
    } else {
      const curveOffset = pathToChildIndex === childTokens.length - 1 ? 0 : CURVE_WIDTH;
      // account for the existing offset applied by the horizontal line component and then add one back in to fill in path from root
      pathLeftOffset = childWidths / 2 - CURVE_WIDTH - 1;
      pathRightOffset += rightWidthSum + targetWidth / 2 - curveOffset - 1;
    }
  }

  const branchCurves = (
    <StepFlowBranchCurves
      branchDirection={branchDirection}
      childTokens={childTokens}
      stepTokenToPositionMap={stepTokenToPositionMap}
      width={width}
      leftPathColor={leftPathColor}
      rightPathColor={rightPathColor}
      pathToChildIndex={pathToChildIndex}
    />
  );

  return (
    <>
      {branchDirection === 'in' && branchCurves}
      <HorizontalBranchingLine
        leftOffset={leftOffset}
        rightOffset={rightOffset}
        width={childWidths}
        leftPathColor={leftPathColor}
        rightPathColor={rightPathColor}
      />
      {drawPath && (
        <PathHorizontalBranchingLineContainer>
          <HorizontalBranchingLine
            leftOffset={pathLeftOffset}
            rightOffset={pathRightOffset}
            width={childWidths}
            leftPathColor={Color.CorePurple600}
            rightPathColor={Color.CorePurple600}
          />
        </PathHorizontalBranchingLineContainer>
      )}
      {branchDirection === 'out' && branchCurves}
    </>
  );
};

export const StepFlowBranchContainer = styled('div')<{ blur: boolean }>`
  opacity: ${({ blur }) => (blur ? 0.25 : 1)};
  transition: opacity 0.5s;
`;

export const StepFlowBranchStepsContainer = styled('div')`
  display: flex;
  justify-content: center;
`;

export const StepFlowBranchSubTree = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StepFlowBranchEmptySpacer = styled('div')<{ width: number }>`
  width: ${({ width }) => width}px;
`;

type StepBase = {
  [key: string]: unknown;
  token: string;
  branches?: StepBase[][];
  branch?: StepBase[];
};

export type StepPosition = {
  width: number;
};

export const buildStepWidths = (
  steps: StepBase[],
  stepContentWidth: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
  collapsedNodeTokens: string[],
): number => {
  if (steps.length === 0) {
    return stepContentWidth + 16;
  }

  const [currentStep, ...restSteps] = steps;

  if (
    Array.isArray(currentStep?.branches) ||
    (Array.isArray(currentStep?.branch) && currentStep.branch.length)
  ) {
    const branches = [
      ...(Array.isArray(currentStep?.branches) ? currentStep.branches : []),
      ...(Array.isArray(currentStep?.branch) ? [[], currentStep.branch] : []),
    ];

    const childrenWidth = collapsedNodeTokens.includes(currentStep.token)
      ? stepContentWidth
      : sum(
          branches.map((branch, index) => {
            const branchWidth = buildStepWidths(
              branch,
              stepContentWidth,
              stepTokenToPositionMap,
              collapsedNodeTokens,
            );
            stepTokenToPositionMap.set(`${currentStep.token}-branch-${index}`, {
              width: branchWidth,
            });
            return branchWidth;
          }),
        );

    const nextWidth = buildStepWidths(
      restSteps,
      stepContentWidth,
      stepTokenToPositionMap,
      collapsedNodeTokens,
    );
    const nodesWidth = Math.max(childrenWidth, nextWidth);
    stepTokenToPositionMap.set(currentStep.token, { width: nodesWidth });
    return nodesWidth;
  } else {
    const nodesWidth = buildStepWidths(
      restSteps,
      stepContentWidth,
      stepTokenToPositionMap,
      collapsedNodeTokens,
    );
    stepTokenToPositionMap.set(currentStep.token, { width: nodesWidth });
    return nodesWidth;
  }
};

export enum PathIndex {
  None = -1,
  All = -2,
}

export interface PathHighlightInformation {
  connectorAfterStepColor: string;
  toOutBranchLineColor: string;
  leftBranchOutConnectorsColor: string;
  rightBranchOutConnectorsColor: string;
  rightBranchInConnectorsColor: string;
  leftBranchInConnectorsColor: string;
  pathToChildOutIndex: number;
  pathToChildInIndex: number;
}
